import {
  UserThreadNotification,
  UserNotification,
} from '@/app/core/providers/UserNotificationProvider'
import { InboxNotificationCustomData } from '@liveblocks/core'
import {
  CommentData,
  ThreadData,
  getMentionedIdsFromCommentBody,
} from '@liveblocks/client'

import { format, intervalToDuration, isBefore, subSeconds } from 'date-fns'

export const getNotificationUrl = (
  crunchUuid: string | undefined,
  surveyUuid: string | undefined,
  thread: ThreadData | undefined,
  comment: CommentData | undefined
) => {
  const searchParams = new URLSearchParams()

  if (thread?.metadata?.type === 'playbook') {
    searchParams.set('infoPanel', 'Cheatsheet')
  } else {
    searchParams.set('infoPanel', 'Checklist')
  }

  if (surveyUuid) {
    if (thread?.metadata?.type === 'playbook') {
      searchParams.set('playbook', surveyUuid)
    } else {
      searchParams.set('checklist', surveyUuid)
    }
  }

  if (thread?.metadata?.questionId) {
    searchParams.set('qId', thread.metadata.questionId)
  }

  return comment
    ? `/crunches/${crunchUuid}?${searchParams}#${comment.id}`
    : `/crunches/${crunchUuid}?${searchParams}`
}

export const isCommentUnread = (
  comment: CommentData,
  notification: UserThreadNotification
) => {
  if (!comment.body) {
    return false
  }

  return notification.readAt
    ? comment.createdAt > notification.readAt &&
        comment.createdAt <= notification.notifiedAt
    : comment.createdAt <= notification.notifiedAt
}

export const isNotificationUnread = (notification: UserNotification) => {
  if (!notification.readAt) {
    // Notification has never been read
    return true
  }

  if (notification.kind === 'thread') {
    // For thread notifications, we check that the most recent not deleted
    // comment is newer than the last time the notification was read
    return Boolean(
      notification.thread?.comments.some(
        (comment) =>
          !comment.deletedAt && comment.createdAt > notification.readAt!
      )
    )
  }

  // For other types of notifications, we check that the notification
  // was triggered after the last time it was read
  return notification.notifiedAt > notification.readAt
}

export const isCommentMentionningUser = (
  comment: CommentData,
  userId: string
) =>
  !!comment.body &&
  getMentionedIdsFromCommentBody(comment.body).includes(userId)

export const formatNotificationDate = (notification: UserNotification) => {
  const notifiedAt = getNotificationDate(notification)

  if (isBefore(notifiedAt, subSeconds(new Date(), 86400))) {
    return format(notifiedAt, 'MMM d')
  }

  const duration = intervalToDuration({
    start: notifiedAt,
    end: new Date(),
  })

  if (duration.hours) {
    return `${duration.hours}h`
  }

  if (duration.minutes) {
    return `${duration.minutes}m`
  }

  return `${duration.seconds}s`
}

/**
 * Format a date for display in notifications
 * @param date - The date to format
 * @returns Formatted date string (e.g. "2h", "5m", "Apr 15")
 */
export const formatDate = (date: Date): string => {
  if (isBefore(date, subSeconds(new Date(), 86400))) {
    return format(date, 'MMM d')
  }

  const duration = intervalToDuration({
    start: date,
    end: new Date(),
  })

  if (duration.hours) {
    return `${duration.hours}h`
  }

  if (duration.minutes) {
    return `${duration.minutes}m`
  }

  return `${duration.seconds}s`
}

export const parseRoomId = (roomId: string | undefined) => {
  if (!roomId) {
    return {
      teamUuid: undefined,
      crunchUuid: undefined,
      checklistUuid: undefined,
    }
  }

  const [teamUuid, crunchUuid, checklistUuid] = roomId.split(':')

  return {
    teamUuid,
    crunchUuid,
    checklistUuid,
  }
}

export const getNotificationDate = (notification: UserNotification) => {
  if (notification.kind !== 'thread') {
    return notification.notifiedAt
  }

  const mostRecentNonDeletedComment = notification.thread?.comments?.reduce<
    CommentData | undefined
  >((prev, curr) => {
    if (curr.deletedAt) {
      return prev
    }

    if (!prev) {
      return curr
    }

    return prev.createdAt > curr.createdAt ? prev : curr
  }, undefined)

  return mostRecentNonDeletedComment?.createdAt || notification.notifiedAt
}

export const isUserAssignedCustomNotification = (
  notification: UserNotification
): notification is InboxNotificationCustomData<'$userAssigned'> =>
  notification.kind === '$userAssigned'

export const isUserUnassignedCustomNotification = (
  notification: UserNotification
): notification is InboxNotificationCustomData<'$userUnassigned'> =>
  notification.kind === '$userUnassigned'

export const isUserAssignmentRemovedCustomNotification = (
  notification: UserNotification
): notification is InboxNotificationCustomData<'$userAssignmentRemoved'> =>
  notification.kind === '$userAssignmentRemoved'

export const isUserThreadNotification = (
  notification: UserNotification
): notification is UserThreadNotification => notification.kind === 'thread'

export const isSurveyNotification = (
  notification: UserNotification,
  checklistId: string
): boolean => {
  if (
    isUserAssignedCustomNotification(notification) ||
    isUserUnassignedCustomNotification(notification)
  ) {
    return notification.activities[0].data.checklistUuid === checklistId
  }

  if (isUserThreadNotification(notification)) {
    const { checklistUuid: roomChecklistId } = parseRoomId(notification.roomId)

    return roomChecklistId === checklistId
  }

  return false
}
